import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { Button, Spinner, StatusSelect, Badge, Icon, StatCard, FilterChip } from '../../components/ds';
import { useToast } from '../../components/ds';
import AdDataRow from './AdDataRow';
import './AdsList.css';

const FILTERS = ['all', 'draft', 'active', 'paused', 'archived'];

// Returns true when reactivation should be blocked:
// status ∈ {ended, archived} AND now (UTC) > end_time + 24h
function isReactivationBlocked(ad) {
  if (!ad || !ad.end_time) return false;
  const endMs = new Date(ad.end_time).getTime();
  if (isNaN(endMs)) return false;
  return Date.now() > endMs + 24 * 60 * 60 * 1000;
}

const REACTIVATION_BLOCKED_TOOLTIP = '結束超過 24 小時，無法重新啟用，請建立新廣告';
const FILTER_LABELS = { all: '全部', draft: '草稿', active: '啟用', paused: '已暫停', archived: '已封存' };

// Data flow (AS-099):
//   Phase 1 — list: GET /portal/ads  → renders rows immediately
//   Phase 2 — metrics: GET /portal/ads/metrics (async, keyed by ad id)
//             → metricsLoading shows skeleton; metricsHidden hides on failure
//   Race guard: fetchGenRef increments on each call; stale responses are dropped.
//   Unmount guard: isMountedRef prevents setState after component teardown.
export default function AdsList() {
  const [ads, setAds] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appsMap, setAppsMap] = useState({});
  // AS-099: metrics state (async second phase)
  const [metricsMap, setMetricsMap] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsHidden, setMetricsHidden] = useState(false);
  const [selectedAppFilter, setSelectedAppFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { addToast } = useToast();
  const companyID = sessionStorage.getItem('portal_company_id');

  // Race condition + unmount guard for fetchMetrics
  const fetchGenRef = useRef(0);
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    if (!companyID) { navigate('/login'); return; }
    fetchAds();
  }, [companyID, navigate]);

  const fetchMetrics = async (adList) => {
    if (!adList || adList.length === 0) return;
    const ids = adList.map((a) => a.id);
    const myGen = ++fetchGenRef.current;
    setMetricsLoading(true);
    setMetricsHidden(false);
    setMetricsMap({});
    try {
      const data = await api.getAdsMetrics({ ids, companyId: companyID });
      // data shape: { "<adId>": { executed_impressions, clicks, click_rate }, ... }
      if (!isMountedRef.current || myGen !== fetchGenRef.current) return;
      setMetricsMap(data || {});
      setMetricsLoading(false);
    } catch (err) {
      if (!isMountedRef.current || myGen !== fetchGenRef.current) return;
      console.warn('Failed to fetch ads metrics:', err);
      addToast('報表載入失敗', 'error');
      setMetricsHidden(true);
      setMetricsLoading(false);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    setError('');
    // Reset metrics state on each fetch
    setMetricsMap({});
    setMetricsHidden(false);
    setMetricsLoading(false);
    try {
      const data = await api.getPortalAds(companyID);
      setAds(data);
      // Fetch applications for "網站" column
      try {
        const appsData = await api.getPortalApplications(companyID);
        const apps = appsData || [];
        setApplications(apps);
        const map = {};
        apps.forEach((a) => { map[a.uuid] = a.app_name; });
        setAppsMap(map);
      } catch {
        // Optional, don't block
      }
      // AS-099: Phase 2 — fetch metrics async after list is rendered
      setLoading(false);
      fetchMetrics(data);
      return; // skip the finally setLoading(false) below
    } catch (err) {
      setError(err.message || '載入廣告失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (adId) => {
    try {
      const result = await api.activatePortalAd(adId);
      if (result.errors && result.errors.length > 0) {
        addToast('部分資源啟用失敗：' + result.errors.join('; '), 'error');
      } else {
        addToast('廣告已啟用！', 'success');
      }
      fetchAds();
    } catch (err) {
      const msg = err.status === 422 ? (err.message || REACTIVATION_BLOCKED_TOOLTIP) : ('啟用失敗：' + (err.message || '未知錯誤'));
      addToast(msg, 'error');
    }
  };

  const handlePause = async (adId) => {
    try {
      await api.pausePortalAd(adId);
      addToast('廣告已暫停', 'success');
      fetchAds();
    } catch (err) {
      addToast('暫停失敗：' + err.message, 'error');
    }
  };

  const handleResume = async (adId) => {
    try {
      await api.resumePortalAd(adId);
      addToast('廣告已恢復投放', 'success');
      fetchAds();
    } catch (err) {
      const msg = err.status === 422 ? (err.message || REACTIVATION_BLOCKED_TOOLTIP) : ('恢復失敗：' + err.message);
      addToast(msg, 'error');
    }
  };

  const handleArchive = async (adId) => {
    try {
      await api.archivePortalAd(adId);
      addToast('廣告已封存', 'success');
      fetchAds();
    } catch (err) {
      addToast('封存失敗：' + err.message, 'error');
    }
  };

  const handleUnarchive = async (adId) => {
    try {
      await api.unarchivePortalAd(adId);
      addToast('廣告已恢復', 'success');
      fetchAds();
    } catch (err) {
      addToast('恢復失敗：' + err.message, 'error');
    }
  };

  const getStatusOptions = (row) => {
    const hasCreatives = (row.creatives_count != null ? row.creatives_count > 0 : !!row.creative_uuid);
    switch (row.status) {
      case 'draft':
        return hasCreatives
          ? [{ value: 'draft', label: '草稿' }, { value: 'active', label: '啟用' }]
          : [{ value: 'draft', label: '草稿' }];
      case 'active':
        return [
          { value: 'active', label: '啟用' },
          { value: 'paused', label: '暫停' },
          { value: 'archived', label: '封存' },
        ];
      case 'paused':
        return [
          { value: 'paused', label: '已暫停' },
          { value: 'active', label: '恢復投放' },
          { value: 'archived', label: '封存' },
        ];
      case 'archived':
        return [
          { value: 'archived', label: '封存' },
          { value: 'paused', label: '恢復' },
        ];
      default:
        return [{ value: row.status, label: row.status }];
    }
  };

  const handleStatusChange = (row, newValue) => {
    if (newValue === row.status) return;
    if (row.status === 'archived' && newValue === 'paused') {
      handleUnarchive(row.id);
    } else if (newValue === 'archived') {
      handleArchive(row.id);
    } else if (newValue === 'active') {
      if (row.status === 'draft') {
        handleActivate(row.id);
      } else {
        handleResume(row.id);
      }
    } else if (newValue === 'paused') {
      handlePause(row.id);
    }
  };

  const formatDateCompact = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}/${mm}/${dd}`;
  };

  const toDateString = (iso) => {
    if (!iso) return null;
    const match = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  const todayDateString = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getExecutionStatus = (ad) => {
    const start = toDateString(ad.start_time);
    const end = toDateString(ad.end_time);
    if (!start || !end) return { label: '—', variant: 'default' };
    const today = todayDateString();
    if (today < start) return { label: '尚未開始', variant: 'default' };
    if (today > end) return { label: '廣告已結束', variant: 'default' };
    return { label: '走期中', variant: 'success' };
  };

  const getCreativeCount = (ad) => {
    const count = ad.creatives_count != null ? ad.creatives_count : (ad.creative_uuid ? 1 : 0);
    if (count === 0) {
      return (
        <span className="creative-count-warning" title="尚未建立廣告素材">
          <Icon name="alert-triangle" size={14} />
          0 個
        </span>
      );
    }
    return <span style={{ whiteSpace: 'nowrap' }}>{count} 個</span>;
  };

  const getActionButtons = (row) => {
    return [
      <Button key="edit" variant="ghost" size="sm"
        onClick={(e) => { e.stopPropagation(); navigate(`/ads/create?edit=${row.id}&step=${row.current_step || 1}`); }}>編輯</Button>,
    ];
  };

  const statusFilteredAds = filter === 'all'
    ? ads.filter((ad) => ad.status !== 'archived')
    : ads.filter((ad) => ad.status === filter);

  const filteredAds = selectedAppFilter
    ? statusFilteredAds.filter((ad) => ad.application_id === selectedAppFilter)
    : statusFilteredAds;

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  // Metrics computed from real ads data
  const totalAds = ads.filter(a => a.status !== 'archived').length;
  const activeAds = ads.filter(a => a.status === 'active').length;
  const draftAds = ads.filter(a => a.status === 'draft').length;
  const pausedAds = ads.filter(a => a.status === 'paused').length;
  const archivedAds = ads.filter(a => a.status === 'archived').length;

  // Per-status counts for FilterChip badges
  const statusCounts = {
    all: totalAds,
    draft: draftAds,
    active: activeAds,
    paused: pausedAds,
    archived: archivedAds,
  };

  return (
    <div className="ads-page">
      {/* Stats Row — DS StatCard */}
      <div className="stats-row">
        <StatCard label="廣告總數（不含封存）" value={totalAds} />
        <StatCard label="啟用中" value={activeAds} />
        <StatCard label="草稿" value={draftAds} />
        <StatCard label="已暫停" value={pausedAds} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1>我的廣告</h1>
        <Button onClick={() => navigate('/ads/create')}>+ 建立廣告</Button>
      </div>

      {/* Filter Chips — DS FilterChip */}
      <div className="filter-bar">
        {FILTERS.map((f) => (
          <FilterChip
            key={f}
            active={filter === f}
            count={statusCounts[f]}
            onClick={() => setFilter(f)}
          >
            {FILTER_LABELS[f]}
          </FilterChip>
        ))}
        {applications.length > 1 && (
          <select
            className="filter-app-select"
            value={selectedAppFilter}
            onChange={(e) => setSelectedAppFilter(e.target.value)}
            aria-label="篩選網站"
          >
            <option value="">全部網站</option>
            {applications.map((app) => (
              <option key={app.uuid} value={app.uuid}>{app.app_name}</option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="error-box">{error}</div>}

      {!error && filteredAds.length === 0 && (
        <div className="empty-state">
          <p>還沒有廣告</p>
          <p className="empty-state__hint">點擊上方「建立廣告」按鈕建立第一個廣告</p>
        </div>
      )}

      {!error && filteredAds.length > 0 && (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th className="table__th" style={{ width: '26%' }}>廣告 / 網站</th>
                <th className="table__th" style={{ width: '8%' }}>版位類型</th>
                <th className="table__th" style={{ width: '12%' }}>狀態</th>
                <th className="table__th" style={{ width: '8%' }}>素材</th>
                <th className="table__th" style={{ width: '14%' }}>執行狀態</th>
                <th className="table__th" style={{ width: '20%' }}>走期</th>
                <th className="table__th" style={{ width: '8%' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredAds.map((row) => (
                <React.Fragment key={row.id}>
                  {/* Primary row */}
                  <tr
                    className="table__row table__row--clickable ads-list__primary-row"
                    onClick={() => navigate(`/ads/${row.id}`)}
                  >
                    <td>
                      <div className="cell-name-site">
                        <div className="cell-name-site__name">{row.name}</div>
                        <div className="cell-name-site__site">{appsMap[row.application_id] || '-'}</div>
                      </div>
                    </td>
                    <td>{row.placement_type_display || '—'}</td>
                    <td>
                      {(() => {
                        const isDraftNoCreatives = row.status === 'draft' &&
                          !(row.creatives_count != null ? row.creatives_count > 0 : !!row.creative_uuid);
                        const blocked = isReactivationBlocked(row);
                        return (
                          <StatusSelect
                            value={row.status}
                            options={getStatusOptions(row)}
                            disabled={isDraftNoCreatives}
                            disabledOptions={blocked ? [{ value: 'active', tooltip: REACTIVATION_BLOCKED_TOOLTIP }] : []}
                            onChange={(e) => { handleStatusChange(row, e.target.value); }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        );
                      })()}
                    </td>
                    <td>{getCreativeCount(row)}</td>
                    <td>
                      {(() => {
                        const { label, variant } = getExecutionStatus(row);
                        return <Badge variant={variant}>{label}</Badge>;
                      })()}
                    </td>
                    <td>
                      {!row.start_time || !row.end_time ? (
                        <span className="cell-date">—</span>
                      ) : (
                        <div className="cell-date">
                          <span>{formatDateCompact(row.start_time)}</span>
                          <span className="cell-date__end">~ {formatDateCompact(row.end_time)}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {getActionButtons(row)}
                      </div>
                    </td>
                  </tr>
                  {/* Data row: progress, pacing, CTR */}
                  <tr
                    className="table__row ads-list__data-row"
                    onClick={() => navigate(`/ads/${row.id}`)}
                  >
                    <td colSpan={7} className="ads-list__data-cell">
                      <AdDataRow
                        ad={row}
                        summary={metricsMap[row.id]}
                        metricsLoading={metricsLoading}
                        metricsHidden={metricsHidden}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
